import {
    createEditSinAndLoanAmountWithCoapplicantSchema,
    ExceptionErrorCode,
    FinancingConfigDto,
    OriginationTracking,
} from '@applyonline/shared'
import { t } from '@if/core/i18n'
import { EBeneficiaryType, ELoanPurpose, getEBeneficiaryTypeList, getELoanPurposeList } from '@if/core/types'
import * as yup from '@if/core/yup'
import { Constants } from '../constants'
import { ApplicantSchema } from './ApplicantSchema'

const specialistRegex = /^([-A-Z\u00C0-\u00DC]|[a-z\u00E0-\u00FC]|['.\s])*?$/
const nameNoDigitsRegex = /^[^0-9]*$/

export const buildPrequalificationSchemaObject = (financingProgramConfig: FinancingConfigDto) => {
  const PrequalificationSchema = yup.object({
    merchantId: yup.string().required(),
    applicant: ApplicantSchema().required().default(ApplicantSchema().getDefault()),
    requestedLoanAmount: yup
      .number()
      .required()
      .min(financingProgramConfig.minimumLoanAmount)
      .max(financingProgramConfig.maximumLoanAmount),
    loanPurposeId: yup
      .mixed<ELoanPurpose>()
      .required()
      .oneOf([...getELoanPurposeList()], ExceptionErrorCode.InvalidLoanPurpose),
    merchantName: yup.string().defined().default(''),
    specialistName: yup.string().nullable().matches(specialistRegex, 'specialistNameErrorMessage'),
    merchantPaymentPlanId: yup.string().nullable(),
    prequalPromoCode: yup.string().nullable(),
    beneficiaryTypeId: yup.numberEnum<EBeneficiaryType>(getEBeneficiaryTypeList()).when('loanPurposeId', {
      is: ELoanPurpose.Veterinary,
      then: (schema) => schema.default(EBeneficiaryType.Other),
      otherwise: (schema) => schema.default(EBeneficiaryType.Applicant),
    }),
    otherBeneficiaryFirstName: yup
      .string()
      .trim()
      .test(
        'validName',
        () => t('common.errors.invalidCharacters'),
        function (value) {
          if (!value || value === '') return true
          return nameNoDigitsRegex.test(value)
        },
      )
      .when(['loanPurposeId', 'beneficiaryTypeId'], {
        is: (loanPurposeId: ELoanPurpose, beneficiaryTypeId: EBeneficiaryType) =>
          loanPurposeId === ELoanPurpose.Veterinary ||
          loanPurposeId === ELoanPurpose.GoodsAndServices ||
          beneficiaryTypeId !== EBeneficiaryType.Other,
        then: (schema) => schema.nullable(),
        otherwise: (schema) => schema.required(),
      }),
    otherBeneficiaryLastName: yup
      .string()
      .trim()
      .test(
        'validName',
        () => t('common.errors.invalidCharacters'),
        function (value) {
          if (!value || value === '') return true
          return nameNoDigitsRegex.test(value)
        },
      )
      .when(['loanPurposeId', 'beneficiaryTypeId'], {
        is: (loanPurposeId: ELoanPurpose, beneficiaryTypeId: EBeneficiaryType) =>
          loanPurposeId === ELoanPurpose.GoodsAndServices || beneficiaryTypeId !== EBeneficiaryType.Other,
        then: (schema) => schema.nullable(),
        otherwise: (schema) => schema.required(),
      }),
    isInvisalign: yup.bool().default(false),
  })
  return PrequalificationSchema
}

export type Prequalification = yup.InferSubmit<ReturnType<typeof buildPrequalificationSchemaObject>> & {
  lastTouchOrigination: OriginationTracking | null
  firstTouchOrigination: OriginationTracking | null
}

const defaultFinancingConfig: FinancingConfigDto = {
  minimumLoanAmount: Constants.MinLoanAmount,
  maximumLoanAmount: Constants.MaxLoanAmount,
} as FinancingConfigDto

const PrequalificationSchema = buildPrequalificationSchemaObject(defaultFinancingConfig)

export const buildLoanAmountInputSchema = (financingProgramConfig: FinancingConfigDto) =>
  buildPrequalificationSchemaObject(financingProgramConfig).pick(['requestedLoanAmount', 'loanPurposeId', 'isInvisalign'])

export const LoanAmountInputSchema = PrequalificationSchema.pick(['requestedLoanAmount', 'loanPurposeId', 'isInvisalign'])
export type LoanAmountInput = yup.InferSubmit<typeof LoanAmountInputSchema>

export const ServiceProviderSchema = PrequalificationSchema.pick([
  'merchantName',
  'merchantId',
  'specialistName',
  'loanPurposeId',
  'isInvisalign',
])
export type ServiceProviderInput = yup.InferSubmit<typeof ServiceProviderSchema>

const applicantWithoutContactSchema = () =>
  ApplicantSchema().pick([
    'firstName',
    'middleName',
    'lastName',
    'genderId',
    'languageId',
    'birthDate',
    'sin',
    'relationWithApplicant',
  ])

const beneficiarySchema = PrequalificationSchema.pick([
  'beneficiaryTypeId',
  'otherBeneficiaryFirstName',
  'otherBeneficiaryLastName',
])

export const PersonalInformationInputSchema = () =>
  yup.object({
    applicant: applicantWithoutContactSchema(),
    ...beneficiarySchema.fields,
  })

export type PersonalInformationInput = yup.InferSubmit<ReturnType<typeof PersonalInformationInputSchema>>

export const buildEditSinAndLoanAmountSchema = (maxLoanAmount: number) =>
  createEditSinAndLoanAmountWithCoapplicantSchema(Constants.MinLoanAmount, maxLoanAmount)

export type EditSinAndLoanAmount = yup.InferSubmit<ReturnType<typeof buildEditSinAndLoanAmountSchema>>